
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Phone, MapPin, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { submitContactMessage, contactFormSchema } from '@/services/contactService';
import { z } from 'zod';

const Contact = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErrors({});

    // Validate form data using Zod schema
    try {
      contactFormSchema.parse(formData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            errors[err.path[0].toString()] = err.message;
          }
        });
        setValidationErrors(errors);
        
        toast({
          title: t('contact.validationError'),
          description: Object.values(errors)[0] || t('contact.checkInputs'),
          variant: 'destructive',
        });
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      await submitContactMessage(formData);
      
      // Clear the form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setValidationErrors({});
      
      toast({
        title: t('contact.thankYou'),
        description: t('contact.thankYouDesc'),
      });
    } catch (error: any) {
      // Handle specific validation errors from server
      if (error instanceof z.ZodError) {
        toast({
          title: t('contact.validationError'),
          description: error.errors[0]?.message || t('contact.checkInputs'),
          variant: "destructive",
        });
      } else {
        toast({
          title: t('contact.sendError'),
          description: error.message || "Something went wrong. Please try again later.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 flex items-center gap-2 hover:bg-white/80"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.backToHome')}
        </Button>

        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-t-2xl">
            <h1 className="text-4xl font-bold mb-4">{t('contact.title')}</h1>
            <p className="text-xl opacity-90">
              {t('contact.subtitle')}
            </p>
          </div>

          <div className="bg-white p-8 rounded-b-2xl shadow-lg">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Information */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  {t('contact.getInTouch')}
                </h2>
                
                <div className="space-y-6">
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-3 text-lg">
                        <Mail className="h-5 w-5 text-blue-600" />
                        {t('contact.email')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-1">info@petius.io</p>
                      <p className="text-sm text-gray-500">{t('contact.emailResponse')}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-3 text-lg">
                        <Phone className="h-5 w-5 text-blue-600" />
                        {t('contact.phone')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 mb-1">+57-314-313-8239</p>
                      <p className="text-sm text-gray-500">{t('contact.phoneHours')}</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-3 text-lg">
                        <MapPin className="h-5 w-5 text-blue-600" />
                        {t('contact.address')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">123 Pet Street<br />San Francisco, CA 94102</p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center gap-3 text-lg">
                        <Clock className="h-5 w-5 text-blue-600" />
                        {t('contact.businessHours')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-1">
                      <p className="text-gray-600">{t('contact.weekdayHours')}</p>
                      <p className="text-gray-600">{t('contact.weekendHours')}</p>
                    </CardContent>
                  </Card>
                </div>

                <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h3 className="font-semibold text-yellow-800 mb-2">
                    {t('contact.emergencySupport')}
                  </h3>
                  <p className="text-sm text-yellow-700">
                    {t('contact.emergencyDescription')}
                  </p>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-6">
                  {t('contact.sendMessage')}
                </h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.name')}
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      placeholder={t('contact.namePlaceholder')}
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={100}
                      className={validationErrors.name ? 'border-destructive' : ''}
                    />
                    {validationErrors.name && (
                      <p className="text-sm text-destructive mt-1">{validationErrors.name}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.email')}
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder={t('contact.emailPlaceholder')}
                      value={formData.email}
                      onChange={handleInputChange}
                      maxLength={255}
                      className={validationErrors.email ? 'border-destructive' : ''}
                    />
                    {validationErrors.email && (
                      <p className="text-sm text-destructive mt-1">{validationErrors.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.subject')}
                    </label>
                    <Input
                      id="subject"
                      name="subject"
                      type="text"
                      required
                      placeholder={t('contact.subjectPlaceholder')}
                      value={formData.subject}
                      onChange={handleInputChange}
                      maxLength={200}
                      className={validationErrors.subject ? 'border-destructive' : ''}
                    />
                    {validationErrors.subject && (
                      <p className="text-sm text-destructive mt-1">{validationErrors.subject}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      {t('contact.message')}
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      rows={6}
                      required
                      placeholder={t('contact.messagePlaceholder')}
                      value={formData.message}
                      onChange={handleInputChange}
                      maxLength={2000}
                      className={validationErrors.message ? 'border-destructive' : ''}
                    />
                    {validationErrors.message && (
                      <p className="text-sm text-destructive mt-1">{validationErrors.message}</p>
                    )}
                    <p className="text-sm text-muted-foreground mt-1">
                      {t('contact.characters', { count: formData.message.length })}
                    </p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? t('contact.sending') : t('contact.sendButton')}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
